import {
  Alert,
  Card,
  Divider,
  Spin,
  Tag,
  Typography,
} from '@surecompanies/core_components'
import React, { useMemo } from 'react'
import {
  CensusFieldCatalogItem,
  CompanyIgnoreRuleVersion,
} from '../../api/company-ignore-api-client'
import styles from './CompanyIgnore.module.scss'
import { formatDate } from './formatDate'

interface ActiveConfigPanelProps {
  activeConfig: CompanyIgnoreRuleVersion | null | undefined
  fields: CensusFieldCatalogItem[]
  loading: boolean
  error?: Error | null
  asCard?: boolean
}

export default function ActiveConfigPanel({
  activeConfig,
  fields,
  loading,
  error,
  asCard = true,
}: ActiveConfigPanelProps) {
  const fieldLabelByKey = useMemo(() => {
    return new Map(fields.map((field) => [field.field, field.field]))
  }, [fields])

  const ignoredFields = activeConfig?.ignoredFields ?? []

  const content = (
    <Spin spinning={loading}>
      <div className={styles.panelHeader}>
        <div>
          <h3 className={styles.panelTitle}>Current Active Config</h3>
          <p className={styles.panelSubtitle}>
            Ignore changes fields where incoming changes are ignored, keeping
            the current DB value.
          </p>
        </div>
        {activeConfig ? (
          <Tag color="#003146">v{activeConfig.version}</Tag>
        ) : null}
      </div>

      <div className={styles.panelBox}>
        {activeConfig ? (
          <>
            <Typography.Paragraph className={styles.panelBoxRow}>
              <Typography.Text strong>Activated:</Typography.Text>{' '}
              {formatDate(activeConfig.activatedAt)}
            </Typography.Paragraph>

            <Divider className={styles.panelDivider} />

            <Typography.Paragraph className={styles.panelBoxRow}>
              <Typography.Text strong>Channels:</Typography.Text>{' '}
              {activeConfig.channels?.length > 0 ? (
                <span className={styles.fieldTags}>
                  {activeConfig.channels.map((channel) => (
                    <Tag key={channel} color="blue">
                      {channel}
                    </Tag>
                  ))}
                </span>
              ) : (
                '—'
              )}
            </Typography.Paragraph>

            <Divider className={styles.panelDivider} />

            <Typography.Paragraph className={styles.panelBoxRow}>
              <Typography.Text strong>Ignored Fields:</Typography.Text>
            </Typography.Paragraph>
            {ignoredFields.length > 0 ? (
              <div className={styles.fieldTags}>
                {ignoredFields.map((fieldKey) => (
                  <Tag key={fieldKey}>
                    {fieldLabelByKey.get(fieldKey) ?? fieldKey}
                  </Tag>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>
                No fields are ignored in the active version.
              </div>
            )}
          </>
        ) : loading ? (
          <div className={styles.emptyState}>Loading active configuration…</div>
        ) : error ? (
          <Alert
            type="error"
            showIcon
            message="Unable to load the active configuration"
            description={error.message}
          />
        ) : (
          <div className={styles.emptyState}>
            No active ignore change rule exists yet. Select fields and save to
            publish the first version.
          </div>
        )}
      </div>
    </Spin>
  )

  return asCard ? <Card>{content}</Card> : content
}
